/**
 * Formateo para pantalla. Todo en `es-PE`.
 *
 * `COD-3`: las fechas de negocio son fechas (AAAA-MM-DD), no instantes. Por eso
 * `fecha()` NO construye un `Date` a partir de la cadena: `new Date('2026-09-18')`
 * se interpreta como medianoche UTC y en Lima (UTC-5) se muestra como el 17.
 * Es un desfase de un día en un sistema donde las fechas tienen consecuencias
 * legales, así que se parte la cadena y se compone a mano.
 */

const MESES = [
  'enero',
  'febrero',
  'marzo',
  'abril',
  'mayo',
  'junio',
  'julio',
  'agosto',
  'septiembre',
  'octubre',
  'noviembre',
  'diciembre',
] as const

/** `2026-09-18` → `18/09/2026`. */
export function fecha(valor: string | undefined): string {
  if (!valor) return '—'
  const [anio, mes, dia] = valor.slice(0, 10).split('-')
  if (!anio || !mes || !dia) return valor
  return `${dia}/${mes}/${anio}`
}

/** `2026-09-18` → `18 de septiembre de 2026`. Para cabeceras y documentos. */
export function fechaLarga(valor: string | undefined): string {
  if (!valor) return '—'
  const [anio, mes, dia] = valor.slice(0, 10).split('-')
  if (!anio || !mes || !dia) return valor
  const nombre = MESES[Number(mes) - 1]
  if (!nombre) return valor
  return `${Number(dia)} de ${nombre} de ${anio}`
}

/** Un instante ISO completo sí lleva zona horaria, así que aquí sí vale `Date`. */
export function instante(valor: string | undefined): string {
  if (!valor) return '—'
  const momento = new Date(valor)
  if (Number.isNaN(momento.getTime())) return valor
  return momento.toLocaleString('es-PE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/** `2026-09` → `Setiembre 2026`. En el Perú se escribe «setiembre». */
export function periodo(valor: string | undefined): string {
  if (!valor) return '—'
  const [anio, mes] = valor.split('-')
  if (!anio || !mes) return valor
  const nombre = MESES[Number(mes) - 1]
  if (!nombre) return valor
  const peruano = nombre === 'septiembre' ? 'setiembre' : nombre
  return `${peruano.charAt(0).toUpperCase()}${peruano.slice(1)} ${anio}`
}

/** `12345678` → `12 345 678`, con espacio fino, para poder leerlo de un vistazo. */
export function dni(valor: string | undefined): string {
  if (!valor) return '—'
  return valor.replace(/(\d{2})(\d{3})(\d{3})/, '$1 $2 $3')
}

export function numero(valor: number | undefined): string {
  if (valor === undefined) return '—'
  return valor.toLocaleString('es-PE')
}

export function porcentaje(valor: number | undefined, decimales = 1): string {
  if (valor === undefined) return '—'
  return `${valor.toFixed(decimales).replace('.', ',')} %`
}

export function bytes(valor: number): string {
  if (valor < 1024) return `${valor} B`
  if (valor < 1024 * 1024) return `${(valor / 1024).toFixed(0)} KB`
  return `${(valor / (1024 * 1024)).toFixed(1).replace('.', ',')} MB`
}

/** Hoy en formato de negocio, en hora local. Para preseleccionar un DatePicker. */
export function hoy(): string {
  const ahora = new Date()
  const mes = String(ahora.getMonth() + 1).padStart(2, '0')
  const dia = String(ahora.getDate()).padStart(2, '0')
  return `${ahora.getFullYear()}-${mes}-${dia}`
}

/** `3` → `3 días hábiles`; `1` → `1 día hábil`; `0` → `hoy vence`. */
export function diasHabiles(valor: number | undefined): string {
  if (valor === undefined) return '—'
  if (valor < 0) return `vencido hace ${Math.abs(valor)} ${Math.abs(valor) === 1 ? 'día hábil' : 'días hábiles'}`
  if (valor === 0) return 'vence hoy'
  return `${valor} ${valor === 1 ? 'día hábil' : 'días hábiles'}`
}

/**
 * Iniciales para el `Avatar` del design system.
 *
 * El `Avatar` pinta su `fallback` **tal cual** dentro de un círculo de 40 px:
 * pasarle el nombre completo lo desborda y se lee como un borrón. Dos letras
 * es lo que cabe.
 */
export function iniciales(nombres: string | undefined): string {
  if (!nombres) return '—'
  const partes = nombres
    .replace(/,/g, ' ')
    .trim()
    .split(/\s+/)
    .filter((parte) => parte.length > 0)
  const primera = partes[0]?.charAt(0) ?? ''
  const segunda = partes.length > 1 ? (partes.at(-1)?.charAt(0) ?? '') : ''
  return `${primera}${segunda}`.toUpperCase()
}

/** Primer apellido + inicial, para migas de pan: «Ríos, M.» */
export function nombreCorto(nombres: string | undefined): string {
  if (!nombres) return '—'
  const partes = nombres.trim().split(/\s+/)
  if (partes.length <= 1) return nombres
  return `${partes[0] ?? ''} ${partes.slice(1).map((parte) => `${parte.charAt(0)}.`).join(' ')}`
}
